import { useKnockFeed } from '@knocklabs/react';
import { parseAsInteger, useQueryState } from 'nuqs';
import { useEffect, useMemo, useState } from 'react';
import { getHighlightFromFeedItem, isRecentlyTriggered, RECENT_TRIGGER_WINDOW_MS } from './feedHighlight';

// Encapsula a leitura do Knock feed: junta os ids que chegam ao vivo pelo socket com os itens
// buscados na montagem que dispararam na janela recente, e devolve o primeiro item que carrega um
// highlight de risco renderizável (`{ item, highlight }`), ou null. Também expõe `feedClient`/`feedItems`
// para as chamadas de engajamento (seen/archived/interacted) que vivem no orquestrador.
const useRiskRecognitionFeedHighlight = () => {
  const { feedClient, useFeedStore } = useKnockFeed();
  const feedItems = useFeedStore((state) => state.items);

  // Override de QA/debug: `?risk-recognition-window-time=<minutos>` alarga (ou encurta) a janela de
  // recência. Sem o param, ou com valor inválido/<=0, cai no default de 10 min.
  const [windowOverrideMinutes] = useQueryState('risk-recognition-window-time', parseAsInteger);
  const recentTriggerWindowMs = windowOverrideMinutes && windowOverrideMinutes > 0 ? windowOverrideMinutes * 60 * 1000 : RECENT_TRIGGER_WINDOW_MS;

  // This card lives on monitoring panels and surfaces "eventos atuais": detections that land over
  // the realtime socket while the screen is open, plus anything triggered in the last 10 min from the
  // page fetched on mount. We collect the live ids from `items.received.realtime` and also accept
  // recently-triggered fetched items, so older history never resurfaces but recent events still do.
  const [realtimeItemIds, setRealtimeItemIds] = useState<ReadonlySet<string>>(() => new Set());

  // Record the ids that arrive live so new events surface immediately. The fetched page (recent
  // history) is handled separately by the 10-min recency gate in the memo below.
  useEffect(() => {
    const collectRealtimeIds = ({ items }: { items: Array<{ id: string }> }) => {
      setRealtimeItemIds((prev) => {
        const next = new Set(prev);
        for (const { id } of items) next.add(id);
        return next;
      });
    };

    feedClient.on('items.received.realtime', collectRealtimeIds);
    return () => feedClient.off('items.received.realtime', collectRealtimeIds);
  }, [feedClient]);

  // The provider only opens the realtime socket; it doesn't fetch on mount. Pull the recent page once
  // so events triggered in the last 10 min (the recency gate) surface on load, not just live arrivals.
  useEffect(() => {
    void feedClient.fetch();
  }, [feedClient]);

  const riskRecognitionFeedItem = useMemo(() => {
    const nowMs = Date.now();
    for (const item of feedItems) {
      if (!(realtimeItemIds.has(item.id) || isRecentlyTriggered(item, nowMs, recentTriggerWindowMs))) continue;
      const highlight = getHighlightFromFeedItem(item);
      if (highlight) return { item, highlight };
    }

    return null;
  }, [feedItems, realtimeItemIds, recentTriggerWindowMs]);

  return { feedClient, feedItems, riskRecognitionFeedItem };
};

export default useRiskRecognitionFeedHighlight;
